import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { URL } from "../config";
import { useOutletContext } from "react-router-dom";

const AppointmentBookingList = () => {
  const { setLoading } = useOutletContext();
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

 
    const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${URL}/appointmentbookings`);
      const bookings = response.data;

      const finalData = await Promise.all(
        bookings.map(async (item) => {
          let cName = "N/A";
          let cMobile = "N/A";

          try {
            const c = await axios.get(`${URL}/customers/${item.fkCustomerId}`);
            cName = c.data?.CustomerName || "N/A";
            cMobile = c.data?.MobileNo || "N/A";
          } catch (err) {
            console.log("Customer fetch error:", err);
          }

          return {
            AppointmentBookingId: item.AppointmentBookingId,
            CustomerName: cName,
            MobileNo: cMobile,
            Address: item.Address,
            AppointmentDate: item.AppointmentDate,
            AppointmentTime: item.AppointmentTime,
            Pincode: item.Pincode,
            Description: item.Description,
            InternetAvailable: item.InternetAvailable,
            Monitoring: item.Monitoring,
            SoftwareUsed: item.SoftwareUsed,
          };
        })
      );

      setDataSource(finalData);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    
    fetchAppointments();

  
    const timer = setTimeout(() => {
      fetchAppointments();
    }, 2000);

   
    return () => clearTimeout(timer);
  }, []);


  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Appointments");

    worksheet.columns = [
      { header: "Booking ID", key: "AppointmentBookingId", width: 15 },
      { header: "Customer Name", key: "CustomerName", width: 20 },
      { header: "Mobile No", key: "MobileNo", width: 15 },
      { header: "Address", key: "Address", width: 25 },
      { header: "Appointment Date", key: "AppointmentDate", width: 18 },
      { header: "Appointment Time", key: "AppointmentTime", width: 18 },
      { header: "Pincode", key: "Pincode", width: 12 },
      { header: "Description", key: "Description", width: 25 },
      { header: "Internet Available", key: "InternetAvailable", width: 18 },
      { header: "Monitoring", key: "Monitoring", width: 15 },
      { header: "Software Used", key: "SoftwareUsed", width: 18 },
    ];

    dataSource.forEach((item) => worksheet.addRow(item));

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "AppointmentBookingList.xlsx"
    );
  };

  
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(18);
    doc.text("Appointment Booking List", 40, 40);

    const head = [[
      "Booking ID",
      "Customer Name",
      "Mobile No",
      "Address",
      "Appointment Date",
      "Appointment Time",
      "Pincode",
      "Internet",
      "Monitoring",
      "Software Used",
    ]];

    const body = dataSource.map(item => [
      item.AppointmentBookingId,
      item.CustomerName,
      item.MobileNo,
      item.Address,
      item.AppointmentDate,
      item.AppointmentTime,
      item.Pincode,
      item.InternetAvailable ? "Yes" : "No",
      item.Monitoring ? "Yes" : "No",
      item.SoftwareUsed ? "Yes" : "No",
    ]);

    autoTable(doc, {
      startY: 60,
      head,
      body,
      headStyles: { fillColor: [23, 66, 83], textColor: "#ffffff" },
      styles: { fontSize: 10, cellPadding: 6 },
      theme: "grid",
    });

    doc.save("AppointmentBookingList.pdf");
  };


  const handlePrint = () => {
    const tableRows = dataSource
      .map(
        (item) => `
      <tr>
        <td>${item.AppointmentBookingId}</td>
        <td>${item.CustomerName}</td>
        <td>${item.MobileNo}</td>
        <td>${item.Address}</td>
        <td>${item.AppointmentDate}</td>
        <td>${item.AppointmentTime}</td>
        <td>${item.Pincode}</td>
        <td>${item.InternetAvailable ? "Yes" : "No"}</td>
        <td>${item.Monitoring ? "Yes" : "No"}</td>
        <td>${item.SoftwareUsed ? "Yes" : "No"}</td>
      </tr>`
      )
      .join("");

    const style = `
      <style>
        @media print { @page { size: landscape; margin: 18mm; } body { -webkit-print-color-adjust: exact !important; } }
        table { width: 100%; border-collapse: collapse; font-size: 14px; }
        th { background: #174253 !important; color: #fff !important; padding: 10px; }
        td { padding: 10px; border-top: 1px solid #ccc; border-bottom: 1px solid #ccc; }
        tr:nth-child(even) td { background: #f5f5f5 !important; }
        h1 { text-align: center; color: #174253; }
      </style>
    `;

    const html = `
      <html>
        <head>${style}</head>
        <body>
          <h1>Appointment Booking List</h1>
          <table>
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Customer Name</th>
                <th>Mobile No</th>
                <th>Address</th>
                <th>Appointment Date</th>
                <th>Appointment Time</th>
                <th>Pincode</th>
                <th>Internet</th>
                <th>Monitoring</th>
                <th>Software Used</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const columns = useMemo(
    () => [
    
      { accessorKey: "AppointmentBookingId", header: "Booking ID", size: 100 },
      { accessorKey: "CustomerName", header: "Customer Name", size: 160 },
      { accessorKey: "MobileNo", header: "Mobile No", size: 130 },
      { accessorKey: "Address", header: "Address", size: 200 },
      {
        accessorKey: "AppointmentDate",
        header: "Appointment Date",
        size: 130,
        Cell: ({ cell }) => {
          const d = new Date(cell.getValue());
          return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth()+1).toString().padStart(2,"0")}/${d.getFullYear()}`;
        },
      },
      {
        accessorKey: "AppointmentTime",
        header: "Appointment Time",
        size: 130,
        Cell: ({ cell }) => {
          const t = cell.getValue();
          if (!t) return "";
          const [h, m] = t.split(":");
          let hour = parseInt(h);
          const ampm = hour >= 12 ? "PM" : "AM";
          hour = hour % 12 || 12;
          return `${hour}:${m} ${ampm}`;
        },
      },
      { accessorKey: "Pincode", header: "Pincode", size: 100 },
      { accessorKey: "Description", header: "Description", size: 200 },
      {
        accessorKey: "InternetAvailable",
        header: "Internet",
        size: 100,
        Cell: ({ cell }) => (cell.getValue() ? "Yes" : "No"),
      },
      {
        accessorKey: "Monitoring",
        header: "Monitoring",
        size: 100,
        Cell: ({ cell }) => (cell.getValue() ? "Yes" : "No"),
      },
      {
        accessorKey: "SoftwareUsed",
        header: "Software Used",
        size: 130,
        Cell: ({ cell }) => (cell.getValue() ? "Yes" : "No"),
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%", bgcolor: "#ffffff", marginTop:"20px", p: { xs: 1, md: 0 }, overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ fontWeight: 700, fontSize: "22px", color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", paddingLeft: "12px", marginBottom:"20px" }}>
        Appointment Booking List
      </Box>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
        <div style={{ position: "relative", width: "100%", marginTop:"10px" }}>
          <div style={{ position: "absolute", top: "10px", right: "20px", left: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, marginBottom: "10px" }}>
            {/* Left Icons */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Tooltip title="Download Excel">
                <IconButton onClick={handleDownloadExcel} sx={{ backgroundColor: "#d4f1d4", color: "#174253", "&:hover": { backgroundColor: "#b5e3b5" } }}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton onClick={handleDownloadPDF} sx={{ backgroundColor: "#ffe2e2", color: "#d32f2f", "&:hover": { backgroundColor: "#ffcccc" } }}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={handlePrint} sx={{ backgroundColor: "#e3f2fd", color: "#174253", "&:hover": { backgroundColor: "#cde4fa" } }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </div>

          
            <TextField
              size="small"
              placeholder="Search bookings..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#174253" }} /></InputAdornment> }}
              sx={{ width: { xs: "200px", sm: "250px" }, "& .MuiOutlinedInput-root": { borderRadius: "25px", height: "38px", backgroundColor: "#fff", boxShadow: "0 0 6px rgba(0,0,0,0.1)", "& fieldset": { borderColor: "#d0d7de" }, "&:hover fieldset": { borderColor: "#174253" } } }}
            />
          </div>
        </div>

        <MaterialReactTable
  columns={columns}
  data={
    [...dataSource] 
      .reverse()   
      .filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(globalFilter.toLowerCase())
      )
  }

  enablePagination={true}

  initialState={{
    pagination: {
      pageSize: 100,   
      pageIndex: 0,
    },
  }}

  muiPaginationProps={{
    rowsPerPageOptions: [10, 25, 50, 100],
    showFirstButton: true,
    showLastButton: true,
  }}

  enableColumnActions={false}
  enableDensityToggle={false}
  enableFullScreenToggle={false}
  enableColumnFilters={false}
  enableHiding={false}
  enableGlobalFilter={false}

  muiTablePaperProps={{
    sx: {
      boxShadow: "none",
      background: "#fff",
      borderRadius: "15px",
    },
  }}

  muiTableContainerProps={{
    sx: {
      backgroundColor: "#fff",
      width: "100%",
      overflowX: "auto",
      borderRadius: "12px",
      "&::-webkit-scrollbar": { height: "6px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#e0e3ea",
        borderRadius: "10px",
      },
    },
  }}

  muiTableHeadCellProps={{
    sx: {
      backgroundColor: "#e0e0e0ff",
      color: "#393e4f",
      fontWeight: 600,
      fontSize: "16px",
      borderBottom: "0.5px solid #e3e5eb",
      borderRight: "none",
      borderLeft: "none",
      textAlign: "left",
      height: "63px",
      paddingTop: "23px",
      marginTop: "15px",
    },
  }}

  muiTableBodyRowProps={{
    sx: {
      backgroundColor: "#fff",
      "&:hover": {
        backgroundColor: "#eff6fe",
      },
      "& td": {
        fontSize: { xs: "12px", sm: "14px" },
        p: "8px",
        borderBottom: "0.5px solid #e3e5eb",
        borderRight: "none",
        borderLeft: "none",
        height: "54px",
      },
    },
  }}
/>

      </Box>
    </Box>
  );
};

export default AppointmentBookingList;
